import type { Dictionary } from './types';

export const en: Dictionary = {
  page_title: 'AI-DLC Framework',
  page_subtitle: 'Deployment flow across NxKMS — AgentSkills Hub — Project Deployment',

  la_badge: 'Layer A',
  la_sub: 'Enterprise Knowledge Management System — standardizing all knowledge sources into Knowledge Nodes',
  la_src_label: 'Knowledge input sources',
  la_s1_t: 'File Upload', la_s1_d: 'Docs, PDF, MD from projects',
  la_s2_t: 'Google Drive', la_s2_d: 'Auto-sync by folder',
  la_s3_t: 'Notion / Slack', la_s3_d: 'Internal docs & discussions',
  la_s4_t: 'Repo / Wiki', la_s4_d: 'CLAUDE.md, AGENTS.md',
  la_flow_label: 'Internal processing flow — team interactions',
  la_f1: 'Upload & ingest', la_f2: 'Normalize → Knowledge Node',
  la_f3: 'Semantic indexing', la_f4: 'Domain tagging',
  la_f5: 'Semantic search', la_f6: 'Project advisor → suggest Skills',
  la_f7: 'Link to AgentSkills Hub',
  la_m1_t: 'Knowledge Repository',
  la_m1_d: 'Consolidates files & Drive content — normalizes into Knowledge Nodes by project domain',
  la_m2_t: 'Project Advisor',
  la_m2_d: 'Recommends Skills by tech stack, domain, and project type at kickoff',

  la_uflow_label: 'User flow — admin configures, project members chat',
  la_uflow_admin_t: 'Admin side',
  la_uflow_user_t: 'Project member side',
  la_uflow_admin_steps: [
    'Upload knowledge source',
    'Tag by project / domain',
    'Create chatbot (general or knowledge-scoped)',
    'Publish internally',
  ],
  la_uflow_user_steps: [
    'Open NxKMS portal',
    'Select project chatbot',
    'Ask question in natural language',
    'Receive cited answer',
  ],
  la_ui_demo_label: 'Preview — project chatbot interface',
  la_ui_sidebar_t: 'Project chatbots',
  la_ui_bots: [
    { name: 'Fintech Payment Bot', desc: 'PCI-DSS domain expert', active: true },
    { name: 'Legacy Migration Bot', desc: 'COBOL → Java context' },
    { name: 'General NTQ Assistant', desc: 'Company-wide Q&A' },
    { name: 'Monitoring / SRE Bot', desc: 'onTune, Beyla, Prometheus' },
  ],
  la_ui_user_msg: 'How does our payment flow handle 3DS challenge failures for JP customers?',
  la_ui_bot_msg: 'Based on PRD-023-payment.md and the SBPayment integration spec, 3DS failures route through the retry queue with a 2-attempt limit. JP customers additionally trigger a fraud-review flag per the Fintech compliance rules.',
  la_ui_sources_t: 'Sources',
  la_ui_sources: ['PRD-023-payment.md', 'sbpayment-integration.md', 'fintech-rules.md'],
  la_ui_input_ph: 'Ask about this project...',

  conn_ab_dn: 'Suggest Skills by project context',
  conn_ab_up: 'Verified Skills → enrich Knowledge Base',
  conn_ab_dn_note: 'NxKMS analyzes the project domain and recommends the best-fit Skills set — triggered automatically at project kickoff.',
  conn_ab_up_note: 'Every reviewed & published Skill is tagged by domain and added back to NxKMS — improving search accuracy for future projects.',

  lb_badge: 'Layer B',
  lb_sub: 'Platform to aggregate, govern & distribute AI Skills — for all NTQ projects',
  lb_official_t: 'Official Skills — verified from makers',
  lb_official_d: 'From organizations & companies building the technology — verified and synced by NTQ dev team',
  lb_see_more: 'Show 40+ creators',
  lb_see_less: 'Hide',
  lb_ntq_t: 'NTQ Customized Skills',
  lb_ntq_d: 'Per tech stack, domain, role — built & maintained by NTQ AI-DLC Team',
  lb_pkg_t: 'Skills Set / Package for specific projects',
  pkg1_t: 'Fintech Package', pkg2_t: 'Legacy Migration', pkg3_t: 'Monitoring / SRE', pkg4_t: 'ISO 27001 / SOC2',
  lb_gov_label: 'Governance & skill quality',
  lb_gov_steps: ['Skill Author creates', 'Reviewer checks', 'Approve & version', 'Publish to Hub', 'Usage analytics'],
  lb_flow_label: 'Team interaction flow in Hub',
  lb_f1: 'Find skill by domain / role', lb_f2: 'Install via CLI (skills.sh)', lb_f3: 'Update CLAUDE.md',
  lb_f4: 'Admin: manage policy & access', lb_f5: 'Track adoption rate', lb_f6: 'Propose new skills from project',
  lb_persona_label: 'Personas',
  lb_p1: 'Skill Author — BA / Architect / Senior Dev',
  lb_p2: 'Skill Consumer — Dev / QA / PM',
  lb_p3: 'Reviewer / Team Lead',
  lb_p4: 'Enterprise Admin',

  lb_uflow_label: 'User flow — from browse to installed in the IDE',
  lb_uflow_steps: [
    'Search by tech / domain / role',
    'Preview skill details',
    'Install via CLI (skills.sh)',
    'Commit .claude/skills to repo',
    'Whole team pulls & uses',
  ],
  lb_ui_demo_label: 'Preview — skills marketplace',
  lb_ui_search_ph: 'Search skills...',
  lb_ui_filter_label: 'Filter',
  lb_ui_filter_chips: ['Fintech', 'Java Spring', 'Vue / React', 'Security', 'Monitoring'],
  lb_ui_cards: [
    { name: 'fintech-payment', tag: 'NTQ · Domain', desc: 'PCI-DSS-aware payment handler with audit log patterns', downloads: '1.2k installs' },
    { name: 'spring-boot-3', tag: 'Official · Java', desc: 'Spring Boot 3.x conventions: Security, JPA, error handlers', downloads: '3.4k installs' },
    { name: 'pr-checklist', tag: 'NTQ · Review', desc: 'Pre-merge self-review checklist + architectural smells', downloads: '5.8k installs' },
    { name: 'playwright-e2e', tag: 'Official · Testing', desc: 'Playwright test generator with fixtures & retry logic', downloads: '2.7k installs' },
  ],
  lb_ui_install_t: 'Install',
  lb_ui_install_hint: 'Copy this command to install in your project',

  conn_bc_dn: 'Install skills into project agent / IDE',
  conn_bc_up: 'Contribute skills distilled from real projects',
  conn_bc_dn_note: 'Tech Lead selects Skills from Hub, runs install into .claude/skills/ — whole team shares via git clone.',
  conn_bc_up_note: 'Devs / BAs identify new best practices during the project — package as a new Skill — submit to AgentSkills Hub for team-wide reuse.',

  lc_badge: 'Layer C',
  lc_title: 'Project Deployment — AI-DLC In Practice',
  lc_sub: 'Running real projects with AI-DLC framework — every project is a continuous improvement loop',
  lc_steps_label: '10 deployment steps — click any step to view the deck content',
  lc_steps: ['Knowledge Foundation', 'Project Structure', 'IDE Mapping', 'Bootstrap', 'Daily Dev Loop', 'Testing', 'Team Standards', 'Security', 'I/O Map', 'Continuous Improvement'],
  lc_steps_th: ['Step', 'Name', 'Owner', 'What AI does', 'Output'],
  lc_steps_data: [
    ['S1', 'Knowledge Foundation', 'Tech Lead / BA', 'Standardize raw docs to Markdown; draft CLAUDE.md/AGENTS.md; build docs/context so AI output quality = knowledge quality', 'CLAUDE.md, AGENTS.md, docs/ folder'],
    ['S2', 'Project Structure', 'Tech Lead', 'Define .claude/ tree (rules, skills, commands), docs/ structure, naming conventions and the shared skills/workflows registry', '.claude/, docs/requirements, docs/architecture committed'],
    ['S3', 'IDE Mapping', 'Dev Team', 'Map the same concept across Claude Code · Cursor · Windsurf (rules, commands, hooks, MCP); standardize on one IDE per team', 'settings.json, .cursor/rules/, .windsurfrules'],
    ['S4', 'Bootstrap', 'Tech Lead + AI', 'AI runs /init to scan codebase → draft CLAUDE.md/AGENTS.md; install + customise base skills; define /feature /fix /review commands', 'CLAUDE.md v1, .claude/skills, .claude/commands'],
    ['S5', 'Daily Dev Loop', 'PM/BA · Dev · AI · CI', 'Ticket → /onboard → task file → /feature (plan + TDD + hooks) → self-review → human diff review → CI → PR ready; sub-flows for specs, coding, asking, review, debug, planning', 'Task file log, code + tests, PR ready'],
    ['S6', 'Testing', 'QA / Dev', 'Three flows: Unit Test (/test), E2E Playwright (/e2e), Requirement-first (/test-plan from PRD acceptance criteria)', 'Test plan, .test.ts, .spec.ts, coverage report'],
    ['S7', 'Team Standards', 'Tech Lead / Reviewer', '4 layers: Source of truth · Gold-standard examples · Hooks+CI enforcement · Skill/Docs PR process — eliminate "3 devs, 3 styles"', 'team-ai-standards/, gold-standard/, Output Consistency Checklist'],
    ['S8', 'Security', 'Security Lead / Dev', '6 defense layers (architecture · git · settings.json permissions · hooks · CI · rules); rules/security.md forces process.env + self-scan for sk-/api_key/password', '.gitignore, deny rules, gitleaks CI, security-hook.sh'],
    ['S9', 'I/O Map', 'BA / Tech Lead', 'Phase-by-phase table of Human Input · AI Work · Human Gate · Output for Knowledge Foundation → Bootstrap → Intake → Design → Implement → Test → Deploy → Improve', 'I/O diagram (Markdown), API contract'],
    ['S10', 'Continuous Improvement', 'All roles', 'No scheduled reviews — triggered by events: AI repeats a mistake, dev explains same context, better pattern found, new tool, incident — update skill/rule immediately', 'Updated CLAUDE.md, new skills to Hub, docs to NxKMS'],
  ],
  lc_flow_label: 'Team interaction flow within the project',
  lc_f1: 'Find skill from Hub', lc_f2: 'Install & config IDE', lc_f3: 'AI agent executes', lc_f4: 'Dev / QA review',
  lc_f5: 'Query NxKMS when needed', lc_f6: 'Iterate & improve skill', lc_f7: 'Contribute skill back to Hub',
  lc_m1_t: 'During project operation',
  lc_m1_d: 'Team identifies new patterns — drafts skill — submits to Hub for review',
  lc_m2_t: 'After project completion',
  lc_m2_d: 'Retrospective — distill lessons learned — package into skill or knowledge node',
  lc_contrib: 'Every role (Dev, BA, QA, PM) can contribute skills to AgentSkills Hub and documents to NxKMS — creating a continuous improvement loop for all of NTQ',

  leg_a: 'NxKMS (knowledge)', leg_b: 'AgentSkills Hub (skills)', leg_c: 'Project Layer (execution)', leg_flow: 'Downstream flow',

  ntq_cat_tech: 'By Tech Stack',
  ntq_cat_domain: 'By Project Domain',
  ntq_cat_role: 'By Role',
  ntq_tags_tech: [
    { t: 'AWS / Azure', s: 'IaC, Lambda, AKS' },
    { t: 'Java Spring', s: 'Boot 3.x, Security' },
    { t: 'Vue / React', s: 'Vue 3, Next.js 14' },
    { t: 'Go backend', s: 'Gin, gRPC, Ent' },
    { t: 'C# .NET', s: 'Core 8, EF' },
    { t: 'Node.js', s: 'Express, Fastify' },
    { t: 'Python', s: 'FastAPI, Django' },
    { t: 'Mobile', s: 'Flutter, Expo' },
  ],
  ntq_tags_domain: [
    { t: 'Fintech', s: 'PCI-DSS, payment' },
    { t: 'Healthcare', s: 'HIPAA, EHR' },
    { t: 'E-commerce', s: 'Shopify, Cart' },
    { t: 'Legacy Migration', s: 'COBOL → Java' },
    { t: 'Monitoring / SRE', s: 'onTune, Beyla' },
    { t: 'Edtech', s: 'LMS, KT' },
    { t: 'JP Enterprise', s: 'SBPayment, Bajie' },
    { t: 'KR Enterprise', s: 'onTune, WhaTap' },
  ],
  ntq_tags_role: [
    { t: 'BA', s: 'Requirement, spec' },
    { t: 'PM / PO', s: 'Roadmap, risk' },
    { t: 'Dev', s: 'Code, review' },
    { t: 'QA / Tester', s: 'Playwright, test plan' },
    { t: 'DevOps', s: 'CI/CD, IaC' },
    { t: 'Security Lead', s: 'OWASP, audit' },
    { t: 'Tech Lead', s: 'Architecture, skill' },
    { t: 'Designer', s: 'Figma, hand-off' },
  ],

  access_title: 'How does the project team access?',
  access_desc: 'Project members can enter through <span class="access-pill access-pill-a">NxKMS</span> to search knowledge and get Skills recommendations, or directly via <span class="access-pill access-pill-b">AgentSkills Hub</span> to find, install and manage Skills — both paths converge into one unified ecosystem.',
  access_or: 'or',
  access_path_a_label: 'NxKMS',
  access_path_a_desc: 'Search enterprise knowledge & get AI-recommended Skills for your project',
  access_path_a_badge: 'Knowledge path',
  access_path_b_label: 'AgentSkills Hub',
  access_path_b_desc: 'Browse, install and manage AI Skills directly for your IDE & project',
  access_path_b_badge: 'Skills path',
  access_note: 'Both paths converge into one unified ecosystem.',

  footer_copy: '© 2026 NTQ Solution JSC — AI-DLC Framework. All rights reserved.',
  footer_version: 'v1.0',

  step_panel_prev: '‹ Prev step',
  step_panel_next: 'Next step ›',
  step_panel_close: 'Close ✕',
  step_panel_open_hint: 'Click a step above to view the deck content inline',

  deck: {
    s0_title: 'AI-DLC In Practice',
    s0_sub: 'How it actually works in your project — step by step',
    s0_msg: '"AI-DLC does not start with code. It starts with standardising knowledge into documents. Everything else depends on that foundation."',

    s1_title: 'Knowledge Foundation',
    s1_sub: 'The most important step — often skipped',
    s1_msg: 'AI output quality = quality of knowledge provided',
    s1_col1_t: 'Raw Knowledge',
    s1_col2_t: 'Standardise →',
    s1_col3_t: 'AI-Ready Input',
    s1_col1: [
      'Codebase without docs',
      'Wiki, email, meeting notes',
      "Tech decisions in people's heads",
      'Business rules only PM knows',
    ],
    s1_col2: [
      'All content → Markdown (.md)',
      'Clear header hierarchy H1→H2→H3',
      'Diagrams in Mermaid (versionable)',
      'Meaningful names: PRD-001-auth.md',
    ],
    s1_col3: [
      'CLAUDE.md / AGENTS.md < 200 lines',
      'rules/ split by concern',
      'skills/ — on-demand modules',
      'docs/context/ — AI-specific context',
    ],

    s1b_title: 'Knowledge Foundation: Tools',
    s1b_sub: 'Two tool groups — explore your codebase and convert documents into AI-readable knowledge',
    s1b_groups: [
      {
        title: 'Knowledge Graph — Visual & Interactive Code Exploration',
        color: '#4F46E5',
        tools: [
          {
            name: 'GitNexus',
            tag: 'Zero-server · Browser-native Knowledge Graph  ⚠ Requires custom parser',
            desc: 'Drop a GitHub repo or ZIP — GitNexus builds an interactive knowledge graph entirely in your browser. Built-in Graph RAG agent for code Q&A. Unsupported languages (COBOL, Delphi, custom DSLs) require writing a custom tree-sitter parser first.',
            usage: 'Use before starting a new project. Write custom parsers for any language/lib not yet supported.',
            url: 'github.com/abhigyanpatwari/GitNexus',
            color: '#4F46E5',
          },
          {
            name: 'Understand-Anything',
            tag: 'Claude Code Skill · Interactive Codebase Knowledge Graph',
            desc: 'Claude Code / Codex skill that turns any codebase into an interactive knowledge graph. Multi-platform. Search, explore, and ask questions about code relationships and call chains.',
            usage: 'Use in Claude Code via /understand-anything command for real-time codebase exploration.',
            url: 'github.com/Lum1104/Understand-Anything',
            color: '#0B8A75',
          },
          {
            name: 'Repomix',
            tag: 'CLI · Pack entire repo into AI-readable single file',
            desc: 'Packs your entire codebase into a single Markdown/XML file optimized for LLM context windows. Supports .repomixignore, security filtering, and token counting. Ideal for sending full project context to AI in one shot.',
            usage: 'Run repomix before any AI session that needs full codebase awareness. Add .repomixignore to exclude generated files.',
            url: 'github.com/yamadashy/repomix',
            color: '#E6522C',
          },
          {
            name: 'code2prompt',
            tag: 'CLI · Source → structured prompt with full context',
            desc: 'Converts source code files into structured prompts with syntax highlighting, tree view, and file metadata. Supports glob patterns to select files. Output is optimized for copy-paste into AI chat or for injection into CLAUDE.md.',
            usage: 'Use to generate focused prompts from selected modules. Pair with /feature to give AI precise context.',
            url: 'github.com/mufeedvh/code2prompt',
            color: '#F59E0B',
          },
        ],
      },
      {
        title: 'Format Conversion — Convert Documents to AI-Readable Markdown',
        color: '#0078D4',
        tools: [
          {
            name: 'MarkItDown (Customised)',
            tag: 'Microsoft · PDF / DOCX / PPTX / Web → Markdown  ⚠ Must customise per project',
            desc: 'Must be customised per project — each project has different formats, section structures and naming conventions. Japanese projects require encoding handling (Shift-JIS/UTF-8), furigana stripping and custom heading re-mapping. Without adapters output Markdown misleads AI.',
            usage: 'Write a project adapter (convert_docs.py). Test on 3–5 representative samples first.',
            url: 'github.com/microsoft/markitdown',
            color: '#0078D4',
          },
          {
            name: 'Docling',
            tag: 'IBM · PDF / DOCX / HTML → structured Markdown + JSON',
            desc: 'IBM open-source parser with native table extraction, figure detection, and document structure preservation. Exports to Markdown, JSON, and Docling Document format. Handles scanned PDFs with built-in OCR. Better structure fidelity than MarkItDown for complex layouts.',
            usage: 'Use for PDFs with complex tables or figures (contracts, specs, reports). Pair with chunking pipeline for RAG ingestion.',
            url: 'github.com/DS4SD/docling',
            color: '#0062A3',
          },
          {
            name: 'Marker',
            tag: 'GPU-accelerated · PDF → high-quality Markdown  ⚠ Requires GPU for speed',
            desc: 'State-of-the-art PDF to Markdown converter using layout detection and OCR models. Handles multi-column, footnotes, math equations, and code blocks. Best quality for academic papers and complex Japanese/Korean documents. GPU recommended for batch processing.',
            usage: 'Use for scanned PDFs or documents where MarkItDown loses structure. Run locally with GPU for batch conversion.',
            url: 'github.com/VikParuchuri/marker',
            color: '#7C3AED',
          },
          {
            name: 'PyMuPDF4LLM',
            tag: 'PyMuPDF · Fast PDF → Markdown with page-aware chunking',
            desc: 'Python library built on PyMuPDF for fast, accurate PDF extraction with LLM-optimized output. Supports page-by-page extraction, header detection, and hyperlink preservation. Significantly faster than Marker for text-heavy PDFs without complex layouts.',
            usage: 'Use as default PDF extractor for text-heavy docs. Switch to Marker only when layout fidelity is critical.',
            url: 'github.com/pymupdf/PyMuPDF',
            color: '#059669',
          },
        ],
      },
    ],
    s1b_flow: ['Source (PDF/DOCX/Repo/URL)', 'Customise adapters per project', 'Extract & convert', 'Structure & index', 'Commit to docs/context/'],
    s1b_note: '⚠ All tools require per-project customisation. Format converters need encoding/layout adapters (especially for Japanese). Knowledge graph tools need custom parsers for unsupported languages. Always test on 3–5 real samples before committing output.',

    s2_title: 'Project Structure',
    s2_sub: '.claude/ + docs/ — every file has a home and a purpose',
    s2_legend: [
      { type: 'always', label: 'Always loaded' },
      { type: 'ondemand', label: 'On-demand' },
      { type: 'manual', label: 'Manual trigger' },
      { type: 'hooks', label: 'Auto-fire hooks' },
      { type: 'ide', label: 'IDE-specific' },
    ],
    s2_tree: [
      { txt: 'project-root/', type: 'root' },
      { txt: '├── CLAUDE.md', type: 'always', note: 'Team-shared entry point — project conventions, stack, non-negotiables (< 200 lines)' },
      { txt: '├── AGENTS.md', type: 'always', note: 'Cross-IDE compatible variant for Cursor/Windsurf' },
      { txt: '├── CLAUDE.local.md', type: 'muted', note: 'Personal overrides (gitignored)' },
      { txt: '├── .claude/', type: 'root' },
      { txt: '│   ├── rules/', type: 'always', note: 'Always-on rules split by concern (security, style, api)' },
      { txt: '│   ├── skills/', type: 'ondemand', note: 'On-demand skills (api-handler, unit-test, ...)' },
      { txt: '│   ├── commands/', type: 'manual', note: '/feature /fix /review /deploy — slash commands' },
      { txt: '│   ├── agents/', type: 'ondemand', note: 'Sub-agents for specialized flows' },
      { txt: '│   └── settings.json', type: 'hooks', note: 'Hooks (Pre/PostToolUse), permissions, deny rules' },
      { txt: '├── .cursor/rules/', type: 'ide', note: 'Cursor equivalent of rules (*.mdc)' },
      { txt: '├── .windsurfrules', type: 'ide', note: 'Windsurf global rules' },
      { txt: '├── .mcp.json', type: 'ondemand', note: 'MCP server configuration' },
      { txt: '├── docs/', type: 'root', note: 'See next slide for details' },
      { txt: '└── src/ · tests/ · ...', type: 'muted', note: 'Standard project code' },
    ],

    s2b_title: 'docs/ Folder Structure',
    s2b_sub: 'Deep dive — every subfolder explained with naming conventions',
    s2b_tree: [
      { txt: 'docs/', type: 'root' },
      { txt: '├── requirements/', type: 'always', note: 'PRD files — one per feature or epic' },
      { txt: '│   ├── PRD-001-user-auth.md', type: 'always', note: 'Problem + objective + AC + out-of-scope' },
      { txt: '│   └── PRD-002-payments.md', type: 'always', note: 'Naming: PRD-[number]-[short-name].md' },
      { txt: '├── architecture/', type: 'root' },
      { txt: '│   ├── decisions/', type: 'always', note: 'ADRs — immutable once approved' },
      { txt: '│   │   ├── ADR-001-auth-strategy.md', type: 'always', note: 'Context + Decision + Alternatives + Consequences' },
      { txt: '│   │   └── ADR-002-database.md', type: 'always', note: 'Naming: ADR-[number]-[topic].md' },
      { txt: '│   ├── api-contracts/', type: 'ondemand', note: 'API schemas per domain' },
      { txt: '│   │   └── auth.md', type: 'ondemand', note: 'Updated when API endpoints change' },
      { txt: '│   └── system-overview.md', type: 'always', note: 'High-level arch with Mermaid diagrams' },
      { txt: '├── tasks/', type: 'root' },
      { txt: '│   ├── active/', type: 'manual', note: 'Current sprint — AI session logs here' },
      { txt: '│   │   └── TASK-101.md', type: 'manual', note: 'Objective + AC + context + test scenarios' },
      { txt: '│   └── done/', type: 'muted', note: 'Completed — moved after merge' },
      { txt: '├── context/', type: 'ondemand', note: 'AI-specific long-term knowledge' },
      { txt: '│   ├── domain-glossary.md', type: 'ondemand', note: 'Business terms — prevents hallucination' },
      { txt: '│   ├── known-issues.md', type: 'ondemand', note: 'Current bugs and workarounds' },
      { txt: '│   ├── business-rules.md', type: 'ondemand', note: 'Rules only PM/BA knows' },
      { txt: '│   └── tool-changelog.md', type: 'ondemand', note: 'When team adopts new approaches' },
      { txt: '└── templates/', type: 'manual', note: 'Copy-paste starters for new files' },
    ],

    s2c_title: 'Essential Skills & Workflows',
    s2c_sub: "Define before Sprint 1 — the team's AI operating system",
    s2c_skills_t: 'Skill categories (what AI can specialize in)',
    s2c_workflows_t: 'Core slash commands (daily use)',
    s2c_skills: [
      { cat: 'Foundation (install first)', items: ['find-skills', 'skill-creator', 'context-builder'], color: 'teal' },
      { cat: 'Coding', items: ['api-handler', 'component', 'service', 'database'], color: 'accent' },
      { cat: 'Testing', items: ['unit-test', 'e2e-playwright', 'test-plan'], color: 'teal' },
      { cat: 'Review', items: ['pr-checklist', 'code-review'], color: 'amber' },
      { cat: 'Infrastructure', items: ['security', 'docker', 'ci-cd'], color: 'purple' },
    ],
    s2c_workflows: [
      { cmd: '/find-skills [topic]', desc: 'Search & discover available skills' },
      { cmd: '/create-skill [name]', desc: 'Scaffold a new skill from template' },
      { cmd: '/feature TASK-xxx', desc: 'Full TDD implementation flow' },
      { cmd: '/fix TASK-xxx', desc: 'Bug fix with regression test' },
      { cmd: '/review', desc: 'PR self-review checklist' },
      { cmd: '/deploy [env]', desc: 'Deploy with pre-flight gates' },
      { cmd: '/test [file]', desc: 'Unit test generation' },
      { cmd: '/test-plan PRD-xxx', desc: 'Spec-first test planning' },
      { cmd: '/onboard TASK-xxx', desc: 'Task intake from ticket' },
      { cmd: '/e2e [scenario]', desc: 'E2E Playwright test' },
      { cmd: '/init', desc: 'Project bootstrap scan' },
      { cmd: '/refactor [scope]', desc: 'Safe refactoring workflow' },
    ],

    s3_title: 'IDE Tool Mapping',
    s3_sub: 'Claude Code · Cursor · Windsurf · Kiro · Antigravity — same concept, different syntax',
    s3_th: ['Concept', 'Claude Code', 'Cursor', 'Windsurf', 'Kiro (AWS)', 'Antigravity'],
    s3_rows: [
      ['Always-on Rules', 'CLAUDE.md\n+ .claude/rules/*.md', '.cursor/rules/*.mdc\n(alwaysApply: true)', '.windsurfrules\nglobal_rules.md', '.kiro/rules/\n*.md files', 'System prompt\n+ context files'],
      ['Path-scoped Rules', 'Subdirectory\nCLAUDE.md', '*.mdc with\nglob patterns', 'Workspace rules\n(glob mode)', 'Scoped *.md\nby directory', 'Directory\ncontext files'],
      ['On-demand Skills', 'skills/\n(auto-activate)', 'rules/ with\n@ruleName manual', 'Memories\n(auto save/recall)', 'Skills/\non-demand', 'Context\nmodules'],
      ['Slash Commands', 'commands/*.md', 'rules/ description\nfield', 'Workflows\n/workflow-name', '/commands\n*.md', 'Slash commands\nin chat'],
      ['Auto Events', 'settings.json\nPre/PostToolUse', 'SKILL.md\nhooks', 'Auto-memory +\nTurbo mode', 'Hooks:\nPre/PostToolUse', 'Workflow\nautomation'],
      ['Sub-agents', 'agents/*.md', 'Background\nAgents', 'Parallel\nCascades', 'Agents/\nspecialized', 'Parallel\nagents'],
      ['Cross-IDE', 'AGENTS.md (root)', 'AGENTS.md (root)', 'AGENTS.md (root)', 'AGENTS.md (root)', 'AGENTS.md (root)'],
      ['MCP', '.mcp.json', '.mcp.json', '.mcp.json', '.mcp.json', '.mcp.json'],
    ],

    s4_title: 'Project Bootstrap',
    s4_sub: 'One-time setup — complete before Sprint 1',
    s4_lanes: ['Tech Lead', 'Dev Team', 'AI Agent', 'Output'],
    s4_steps: [
      { row: 0, lane: 0, text: 'Define tech stack\nbusiness domain\ncoding conventions', type: 'io' },
      { row: 0, lane: 2, text: '/init → scan codebase\nDetect frameworks, patterns\ndependencies, test setup', type: 'step' },
      { row: 1, lane: 3, text: 'CLAUDE.md draft\nAGENTS.md draft', type: 'io' },
      { row: 1, lane: 0, text: 'Review draft\nAdd business context\nAdd NEVER-do rules\nKeep < 200 lines', type: 'gate' },
      { row: 2, lane: 3, text: 'CLAUDE.md v1 ✓\nAGENTS.md v1 ✓', type: 'note' },
      { row: 2, lane: 0, text: 'Search & install base skills\nfrom skills.sh + official repos\n→ customize for project', type: 'step' },
      { row: 2, lane: 2, text: 'Customise skills:\n+ project patterns\n+ Gotchas section\n+ examples/ folder', type: 'step' },
      { row: 3, lane: 3, text: '.claude/skills/ ✓\n.cursor/rules/ ✓', type: 'note' },
      { row: 3, lane: 0, text: 'Define commands:\n/feature /fix\n/review /deploy', type: 'step' },
      { row: 3, lane: 2, text: 'Draft command files\nConfig hooks in\nsettings.json', type: 'step' },
      { row: 4, lane: 3, text: '.claude/commands/ ✓\nsettings.json ✓', type: 'note' },
      { row: 4, lane: 0, text: 'Commit .claude/ to git\nAll team clone → ready', type: 'gate' },
      { row: 5, lane: 3, text: 'Full team ready ✓', type: 'note' },
    ],

    s5_title: 'Daily Dev Loop',
    s5_sub: 'Feature development swimlane — repeated every day',
    s5_lanes: ['PM / BA', 'Dev', 'AI Agent', 'CI / CD', 'Output'],
    s5_steps: [
      { row: 0, lane: 0, text: 'Create ticket #101\nwith acceptance criteria', type: 'io' },
      { row: 0, lane: 1, text: '/onboard TASK-101', type: 'step' },
      { row: 0, lane: 2, text: 'Read ticket via MCP\nScan related files\nIdentify ambiguities', type: 'step' },
      { row: 1, lane: 2, text: 'Ask clarifying questions', type: 'step' },
      { row: 1, lane: 0, text: 'Answer questions\nConfirm scope', type: 'gate' },
      { row: 1, lane: 3, text: 'CI warms up', type: 'note' },
      { row: 2, lane: 2, text: 'Create TASK-101.md:\n• Objective + AC\n• Tech notes\n• Test scenarios', type: 'step' },
      { row: 2, lane: 1, text: 'Review task file ✓\n/feature TASK-101', type: 'gate' },
      { row: 2, lane: 2, text: 'Load rules + skills\n→ PLAN MODE:\npropose approach', type: 'step' },
      { row: 3, lane: 1, text: 'Approve plan\nor redirect', type: 'gate' },
      { row: 3, lane: 2, text: '[HOOK: PreToolUse]\n→ Write failing tests\n→ Implement\n[HOOK: PostToolUse]', type: 'hooks' },
      { row: 4, lane: 2, text: 'Self-review with\npr-checklist skill\nUpdate TASK-101 log', type: 'step' },
      { row: 4, lane: 1, text: 'Review diff\nTest AC manually\nPush PR', type: 'gate' },
      { row: 4, lane: 3, text: 'CI: unit tests\nlint · secret scan\ncoverage check', type: 'step' },
      { row: 4, lane: 4, text: 'Code ✓  Tests ✓\nPR ready ✓', type: 'io' },
    ],

    s5b_title: 'Effective Daily Prompts',
    s5b_sub: 'What to include in your instructions for the best AI output',
    s5b_anat_t: 'Anatomy of a Good Prompt',
    s5b_examples_t: 'Real Examples (Copy & Paste)',
    s5b_anat: [
      { t: 'Context', d: 'What are we doing? Which files are relevant?' },
      { t: 'Target Action', d: 'What exactly should the AI do? (/feature, /refactor)' },
      { t: 'Constraints', d: 'What to avoid? Which rules to follow?' },
    ],
    s5b_examples: [
      { tag: 'Feature Implementation', body: '/feature TASK-xxx\nPlease implement the UserProfile component based on PRD-001. Use Tailwind for styling. Do not mutate state directly; follow our React performance standards in docs/rules/.' },
      { tag: 'Refactoring', body: '/refactor\nRefactor this file to extract the data fetching logic into a custom React hook (useUserProfile). Keep the existing tests passing. No UI changes.' },
      { tag: 'Debugging', body: '/fix\nThe API returns 401 when the token expires. Look at auth.ts and axios interceptors. Add automatic token refresh logic based on ADR-002-auth-strategy.md.' },
    ],

    s5c_title: 'Specs & Brainstorm Flow',
    s5c_sub: 'From idea to locked requirements — AI as your thinking partner',
    s5c_lanes: ['BA / PM', 'AI Assistant', 'Human Gate'],
    s5c_steps: [
      { lane: 0, row: 0, text: 'Write initial feature brief\nor user story', type: 'io' },
      { lane: 1, row: 0, text: 'Parse brief → clarify intent\n→ suggest use cases', type: 'step' },
      { lane: 2, row: 0, text: 'Reviewer: accepts or\nrejects draft', type: 'gate' },
      { lane: 0, row: 1, text: 'Provide domain context\n+ target audience + constraints', type: 'step' },
      { lane: 1, row: 1, text: 'Generate acceptance criteria\n+ edge cases + out-of-scope', type: 'step' },
      { lane: 2, row: 1, text: 'Tech Lead review:\napprove AC → lock PRD-xxx.md', type: 'gate' },
      { lane: 0, row: 2, text: 'PRD-xxx.md committed\nto docs/requirements/', type: 'io' },
      { lane: 1, row: 2, text: 'Auto-generate TASK-xxx.md\nfrom PRD', type: 'step' },
      { lane: 2, row: 2, text: 'Sprint ready ✓\nBacklog updated ✓', type: 'note' },
    ],
    s5c_tips: [
      'Include in prompt: current draft text · target audience · business constraints · what AI must NOT assume',
      'Example opener: "Here is the draft PRD for the payment module. Target: B2B clients. Constraint: must comply with PCI-DSS. Please identify missing requirements and edge cases."',
      'What AI does best: spot ambiguous requirements · suggest negative test cases · draft AC from narrative · detect conflicting goals',
    ],

    s5d_title: 'Coding Flow',
    s5d_sub: 'Task → Plan → Implement → Review — AI pair programming with guardrails',
    s5d_lanes: ['Human', 'Plan', 'Implement', 'Verify'],
    s5d_steps: [
      { lane: 0, row: 0, text: '/feature TASK-101\n(loads task file\n+ relevant skills)', type: 'step' },
      { lane: 1, row: 0, text: 'AI reads rules/\n+ skills/ → proposes approach', type: 'step' },
      { lane: 0, row: 1, text: 'Review plan:\napprove ✓ or correct ✗', type: 'gate' },
      { lane: 2, row: 1, text: 'AI writes failing\ntests first (TDD)', type: 'step' },
      { lane: 2, row: 2, text: 'AI implements\nfeature to pass tests', type: 'step' },
      { lane: 3, row: 2, text: 'Hooks: auto-format\nlint check\n→ block if fail', type: 'hooks' },
      { lane: 0, row: 3, text: 'Human reviews diff\n+ checks AC → PR ready', type: 'gate' },
      { lane: 3, row: 3, text: 'CI: tests + scan\n→ merge if pass', type: 'io' },
    ],
    s5d_tips: [
      '/feature prompt: "Implement TASK-101. Follow docs/rules/react-perf.md. Use hooks, no class components. Out of scope: payment logic. Stop and ask before touching auth.ts."',
      '/fix prompt: "Bug: API returns 401 on token expiry. Symptom: login page appears mid-session. Hypothesis: interceptor not retrying. Look at src/api/interceptors.ts first."',
      '/refactor prompt: "Extract data-fetching from UserProfile.tsx into useUserProfile hook. Keep all tests passing. Do NOT change any JSX layout or props interface."',
    ],

    s5e_title: 'Asking Flow',
    s5e_sub: 'Understanding the codebase without changing it',
    s5e_lanes: ['Developer', 'AI Agent', 'Gate'],
    s5e_steps: [
      { lane: 0, row: 0, text: 'Load context files:\n@file.ts @ADR-00x.md', type: 'io' },
      { lane: 1, row: 0, text: 'Confirm scope:\nask if files need clarification', type: 'step' },
      { lane: 0, row: 1, text: "Pose question:\n'How does X work?'\n'What breaks if I change Y?'", type: 'step' },
      { lane: 1, row: 1, text: 'Trace code flow step by step\nCite line numbers', type: 'step' },
      { lane: 2, row: 1, text: 'Dev verifies AI answer\nvs actual code', type: 'gate' },
      { lane: 0, row: 2, text: 'If wrong: correct + re-ask\nIf correct: proceed', type: 'step' },
      { lane: 1, row: 2, text: 'No code produced\n— answer only', type: 'note' },
    ],
    s5e_tips: [
      'Always add "Do NOT suggest code changes" in every exploratory prompt',
      'Always use @file references — prevents AI hallucinating about code it has not seen',
      "Don't just ask WHAT — ask WHY. \"Explain WHY the token is stored in memory, not cookie\"",
    ],

    s5f_title: 'Reviewing Flow',
    s5f_sub: 'AI pre-review before human reviewers — structured output',
    s5f_lanes: ['Developer', 'AI Agent', 'Human Reviewer'],
    s5f_steps: [
      { lane: 0, row: 0, text: 'Stage changes:\ngit add -p (selective)', type: 'io' },
      { lane: 0, row: 1, text: 'Run: /review\n@docs/rules/code-style.md\n@docs/rules/security.md', type: 'step' },
      { lane: 1, row: 1, text: 'Analyze staged diff\nMap against rules + ADRs', type: 'step' },
      { lane: 1, row: 2, text: 'Output:\nBLOCKER (must fix)\nWARNING (consider)\nSUGGESTION (optional)', type: 'step' },
      { lane: 0, row: 2, text: 'Fix all BLOCKERs\nPush + open PR', type: 'gate' },
      { lane: 2, row: 3, text: 'Review:\nbusiness logic\ndesign decisions', type: 'gate' },
      { lane: 2, row: 3, text: 'Merge ✓ or request changes', type: 'io' },
    ],
    s5f_tips: [
      'AI checks: rule violations · missing error handling · security issues · naming / pattern deviation',
      'Human focuses on: business logic correctness · design decisions · knowledge transfer · new patterns',
      'Review prompt: "Review staged diff. Output: BLOCKER | WARNING | SUGGESTION. Do NOT flag style issues handled by Prettier."',
    ],

    s5g_title: 'Debugging Flow',
    s5g_sub: 'Systematic bug investigation — structured context for precise diagnosis',
    s5g_lanes: ['Developer', 'AI Agent', 'Output'],
    s5g_steps: [
      { lane: 0, row: 0, text: 'Reproduce bug\ncollect: error + stack trace', type: 'io' },
      { lane: 0, row: 1, text: 'Load: TASK-xxx.md\n+ suspect files + ADRs', type: 'step' },
      { lane: 0, row: 1, text: 'State hypothesis\n+ safe zone (do NOT touch X)', type: 'step' },
      { lane: 1, row: 2, text: 'Trace call chain\nIdentify root cause\nPropose fix options', type: 'step' },
      { lane: 0, row: 2, text: 'Validate diagnosis\nbefore accepting fix', type: 'gate' },
      { lane: 1, row: 3, text: 'Implement fix within\nsafe zone boundaries', type: 'step' },
      { lane: 2, row: 3, text: 'Run unit tests ✓\nAdd regression test case ✓', type: 'io' },
    ],
    s5g_tips: [
      'Bug context: symptom + error message (full stack trace) + reproduction steps',
      'Hypothesis: your best guess — even wrong hypotheses help AI narrow down faster',
      'Safe zone: "Do NOT modify: LoginPage, authSlice.ts" — prevents AI over-reaching',
    ],

    s5h_title: 'Planning Flow',
    s5h_sub: 'Decompose work into small, verifiable tasks — clear input/output for AI execution',
    s5h_lanes: ['Tech Lead / Dev', 'AI Agent', 'Gate'],
    s5h_steps: [
      { lane: 0, row: 0, text: 'Load: PRD-xxx.md\n+ architecture docs\n+ existing tasks', type: 'io' },
      { lane: 0, row: 0, text: "Describe the goal:\n'Build feature X'\nor 'Refactor module Y'", type: 'step' },
      { lane: 1, row: 1, text: 'Break into sub-tasks\neach with:\n- Input files\n- Output files\n- Verify checklist', type: 'step' },
      { lane: 0, row: 1, text: 'Review task plan:\nIs each task small enough?\n(< 1 session)', type: 'gate' },
      { lane: 1, row: 2, text: 'For each sub-task:\nGenerate TASK-xxx.md\nwith full template', type: 'step' },
      { lane: 0, row: 2, text: 'Validate: does each task\nhave clear AC + verify steps?', type: 'gate' },
      { lane: 2, row: 3, text: 'Approved plan →\nexecute tasks in sequence', type: 'io' },
    ],
    s5h_tips: [
      'Size rule: if AI needs >1 session, break further. Each task: 1 input → 1 output → 1 verify command',
      'Task template: Goal · Input files · Output files · Acceptance Criteria · Verify command',
      "Dependency order: order tasks so each one's output is the next one's input. No circular dependencies.",
    ],

    s6_title: 'Testing Workflow',
    s6_sub: 'Unit test · E2E Playwright · Test-first from requirements',
    s6_flows: [
      {
        t: 'Flow A: Unit Test',
        steps: [
          'Dev: /test [file]',
          'AI: load unit-test skill, analyse functions + error paths',
          'AI: generate TEST PLAN (no code yet — review first)',
          'Dev: review plan ✓, add/remove scenarios',
          'AI: write + run tests, fix if fail',
          'HOOK: coverage check — block if below threshold',
          'Output: .test.ts ✓  Coverage ✓',
        ],
      },
      {
        t: 'Flow B: E2E Playwright',
        steps: [
          'Dev: /e2e "user checkout"',
          'AI: load e2e skill, map story → POM pages',
          'AI: create Page Objects, write test scenarios',
          'AI: run headless — pass/fix loop',
          'CI: run E2E suite on push to staging',
          'Block merge if fail',
          'Output: .spec.ts ✓  CI report ✓',
        ],
      },
      {
        t: 'Flow C: Requirement → Tests',
        steps: [
          'BA: write PRD-xxx.md with acceptance criteria',
          'Dev: /test-plan PRD-xxx',
          'AI: extract AC → generate positive + negative + edge',
          'Dev: review test plan ✓ (discover ambiguities!)',
          'BA: clarify ambiguities',
          'AI: write failing tests (stubs)',
          'Dev: implement → tests gradually pass → all green ✓',
        ],
      },
    ],

    s7_title: 'Team Standardisation',
    s7_sub: 'Ensuring every developer produces consistent, reusable output',
    s7_problem_t: 'The broken state',
    s7_problem: [
      'Dev A (Cursor) → style X',
      'Dev B (Claude Code) → style Y',
      'Dev C (no rules) → style Z',
      '→ Extra review time',
      '→ Growing inconsistency',
      '→ Hard onboarding',
    ],
    s7_solution_t: 'Solution: 4-layer shared standards',
    s7_solution: [
      'Layer 1 — Source of Truth: team-ai-standards/ shared git repo (AGENTS.md + rules/ + skills/ + templates/)',
      'Layer 2 — Gold Standard: gold-standard/examples/ (perfect api-handler.ts, unit.test.ts for AI to learn from)',
      'Layer 3 — Enforce via Hooks + CI: pre-commit gitleaks, PostToolUse auto-format, CI lint/naming/coverage gates',
      'Layer 4 — Skill & Docs PR process: all changes via PR, Tech Lead reviews, merged → team auto-updates',
      'Output Consistency Checklist: naming · pattern · error handling · commit convention · task log · API contract · ADR',
    ],

    s8_title: 'Security: Credentials & Secrets',
    s8_sub: 'Defense-in-depth — never rely on a single layer',
    s8_layers: [
      { t: 'Layer 1 — Architecture', d: 'Use secrets manager (AWS / Azure KV / Vault). .env.example committed (no real values). .env.local gitignored.' },
      { t: 'Layer 2 — Git', d: '.gitignore: .env .env.* *.pem *.key secrets/ credentials.json CLAUDE.local.md .claude/memory.json · pre-commit: gitleaks scan' },
      { t: 'Layer 3 — Permissions (settings.json)', d: 'deny: ["Read(./.env)","Read(./secrets/**)","Bash(curl *)","Bash(cat *.env)"]' },
      { t: 'Layer 4 — Hooks', d: 'security-hook.sh: block AI if it tries to read .env / *.key / ~/.aws. Exit 2 + notify.' },
      { t: 'Layer 5 — CI/CD Gate', d: 'gitleaks + Trufflehog on every PR. Block merge if secret pattern detected.' },
      { t: 'Layer 6 — Rules + Training', d: 'rules/security.md: never hardcode. Always process.env.X. AI self-scans for sk-, api_key=, password=.' },
    ],
    s8_rules_t: 'rules/security.md — AI must follow',
    s8_rules: [
      'NEVER write API keys / passwords inline — use process.env.VARIABLE_NAME',
      'ALWAYS add new vars to .env.example (no real value), document in README',
      'Self-scan all generated code for: sk-, pk_, api_key=, password=, secret=, token=',
      'If secret pattern found in own output → STOP, refactor to env var, report',
      'NEVER read .env files — reference docs/context/ for env structure only',
    ],

    s9_title: 'Input / Output Map',
    s9_sub: 'Every phase — who does what, what goes in, what comes out',
    s9_th: ['Phase', 'Human Input', 'AI Does', 'Human Gate', 'Output'],
    s9_rows: [
      ['Knowledge\nFoundation', 'Codebase\nraw docs\ntribal knowledge', '/init → extract patterns\nGenerate CLAUDE.md draft', 'Review + approve\nAdd context', 'docs/ folder\nCLAUDE.md v1'],
      ['Bootstrap', 'Tech stack\nconventions\ncommunity repos', 'Customise skills\nDraft commands + hooks', 'Review skills\nTest commands\nApprove hooks', '.claude/skills/\n.claude/commands/\nsettings.json'],
      ['Task\nIntake', 'Ticket URL or brief', 'Read ticket via MCP\nAsk clarifying Qs\nCreate task file', 'Review task file\nApprove', 'TASK-xxx.md\ndocs/tasks/'],
      ['Design', 'Approved task file', 'Propose approach\nCheck vs ADRs\nList files to change', 'Approve plan\nor redirect', 'Design notes\nUpdated task file'],
      ['Implement', '/feature TASK-xxx', 'Load skills + rules\nTDD: tests first\nImplement + self-review', 'Review diff\nTest AC manually', 'Source code\nUnit tests\nUpdated task log'],
      ['Testing', '/test [file]\n/e2e [scenario]', 'Generate test plan\nAwait approval\nWrite + run tests', 'Review scenarios\nApprove', 'Test files\nCoverage report'],
      ['Deploy', '/deploy [env]', 'Pre-deploy checklist\nE2E on staging\nSecret scan\nRelease notes', 'Final approve', 'Deploy log\nE2E results\nRelease notes'],
      ['Improve', 'Any time\n(bugs, patterns, new tools)', 'Suggest updates on\nrepeat patterns / errors', 'Review + merge\nimprovements', 'Updated CLAUDE.md\nNew/updated skills'],
    ],

    s10_title: 'Continuous Improvement Loop',
    s10_sub: 'Always-on — no fixed schedule, triggered by real events',
    s10_not_t: 'NOT this',
    s10_not: [
      'No sprint review for docs',
      'No weekly update meetings',
      'No scheduled rules maintenance',
    ],
    s10_triggers_t: 'Event-driven triggers',
    s10_triggers: [
      'AI makes same mistake 2+ times → immediately update Gotchas in the skill',
      'Dev explains same context repeatedly → add to CLAUDE.md or skill',
      'Better pattern discovered → update skill, commit, team auto-gets it',
      'New tool / model released → evaluate, update workflow if needed',
      'Bug or security incident → add to known-issues.md / update security rule',
      'Tech Lead owns CLAUDE.md · Senior Devs own specific skills · All Devs report Gotchas via PR · AI proactively suggests improvements',
    ],
  },
};
